import { useQuery } from '@tanstack/react-query';
import { useAccount } from 'wagmi';
import { doFetch } from 'src/utils';
import { TokenInfo, TokenBalance } from 'src/types';

const DEX_API_URL = import.meta.env.VITE_DEX_API_URL;

function mockApiCall<T>(data: T, url?: string) {
  if (url) {
    fetch(url);
  }

  return new Promise<T>((resolve) => {
    setTimeout(
      () => {
        resolve(data);
      },
      100 + Math.random() * 1300
    );
  });
}

export function useUserBalances() {
  const { address } = useAccount();

  const { data, isLoading, error } = useQuery({
    queryKey: ['userBalances', address],
    queryFn: (): Promise<{ data: TokenBalance[] }> =>
      doFetch(`${DEX_API_URL}/balances/${address}`),
    enabled: !!address,
  });

  return {
    userBalances: data?.data,
    userBalancesMapping: data?.data?.reduce(
      (acc, balance) => {
        acc[balance.contractAddress.toLowerCase()] = balance;
        return acc;
      },
      {} as Record<string, TokenBalance>
    ),
    isLoadingUserBalances: isLoading,
    userBalancesError: error,
  };
}

export function useTokensInfo() {
  const {
    data: _data,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['tokensInfo'],
    queryFn: (): Promise<{ data: { metadata: TokenInfo }[] }> =>
      doFetch(`${DEX_API_URL}/tokens`),
    gcTime: 1000 * 60 * 5,
    staleTime: 1000 * 60 * 5,
  });

  const data = _data?.data?.map((token) => token.metadata);

  const getTokenByAddress = (address?: string) => {
    return data?.find((token) => token.contract_address === address);
  };

  const getTokenBySymbol = (symbol?: string) => {
    return data?.find(
      (token) => token.symbol.toUpperCase() === symbol?.toUpperCase()
    );
  };

  return {
    tokensInfo: data,
    isLoadingTokensInfo: isLoading,
    tokensInfoError: error,
    getTokenByAddress,
    getTokenBySymbol,
  };
}

export function useTokensPrices({
  enabled = true,
}: { enabled?: boolean } = {}) {
  const { data, isLoading, error } = useQuery({
    queryKey: ['tokensPrices'],
    queryFn: (): Promise<Record<string, string>> =>
      doFetch(`${DEX_API_URL}/prices`),
    enabled,
  });

  return {
    tokensPrices: data,
    isLoadingTokensPrices: isLoading,
    tokensPricesError: error,
  };
}

type StatValue = {
  timestamp: number;
  value: string;
};

export function useProtocolStats() {
  const { data, isLoading, error } = useQuery({
    queryKey: ['protocolStats'],
    queryFn: () =>
      mockApiCall({
        dailyTVL: [],
        historicalTVL: {
          month: [],
          year: [],
          max: [],
        },
      }),
  });

  return {
    protocolStats: data,
    isLoadingProtocolStats: isLoading,
    protocolStatsError: error,
  };
}

export function useExploreStats() {
  const { data, isLoading, error } = useQuery({
    queryKey: ['exploreStats'],
    queryFn: () =>
      mockApiCall({
        totalLiquidity: '1234567.89',
        volume24h: '54321.01',
        fees24h: '123.45',
        apr: '15.67',
      }),
  });

  return {
    exploreStats: data,
    isLoadingExploreStats: isLoading,
    exploreStatsError: error,
  };
}
